import { Location } from "@angular/common";
import {
	Component,
	computed,
	inject,
	OnInit,
	signal,
	viewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { GroupType } from "@app/enums";
import { Question, SurveyStore } from "@app/stores/survey.store";
import { UserStore } from "@app/stores/user.store";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BackButtonComponent } from "@shared/components/back-button/back-button.component";
import { TrainingService } from "src/services/training/training.service";
import { ApiService } from "../../../services/api.service";
import { CompanyService } from "../../../services/company.service";
import { HealthpartnerService } from "../../../services/healthpartner.service";
import { HealthprofilService } from "../../../services/healthprofil.service";
import { UserService } from "../../../services/user.service";
import { UserStorageService } from "../../../services/user-storage.service";
import { SurveyQuestionsComponent } from "../surveyquestions/survey-questions.component";

@Component({
	selector: "app-surveylast",
	templateUrl: "./surveylast.component.html",
	styleUrls: ["./surveylast.component.scss"],
	imports: [TranslatePipe, BackButtonComponent],
})
export class SurveylastComponent implements OnInit {
	private router = inject(Router);
	private trainingService = inject(TrainingService);

	readonly surveyQuestionComponent =
		viewChild.required<SurveyQuestionsComponent>("surveyquestions");

	public apiservice: ApiService;
	public userdataService: UserService;
	public companyservice: CompanyService;
	public healthpartnerService: HealthpartnerService;
	public translate: TranslateService;
	public userservice: UserStorageService;
	public healthprofilService: HealthprofilService;
	location: Location;
	public start: number = 1;
	public finishedAPV: boolean = false;
	public questionGroups: any;
	public currentId: number = 0;
	public end: number = 0;
	public progress: any;
	public question: any;
	public questionGroup: any;
	public groupQuestionCount: number = 0;
	public groupCurrentQuestionNum: any;
	public groupProgress: any;
	public answer: any;
	public progressMinutesLeft: any;
	public answerTimePerQuestion: any;
	public nextTimer: any;
	public enableNextButton: boolean = false;
	public pleaseWait: boolean = false;
	public Preparing: boolean = false;
	public finishedWellBeing: boolean = false;
	public saveInformationForWellbeing: boolean = false;
	public hasWellBeingFollowUp: boolean = false;
	public showCounselDiv: boolean = false;
	public followUpOnWellBeing: boolean = false;
	public finishedOffendingAct: boolean = false;
	public testMode: boolean = false;

	readonly #surveyStore = inject(SurveyStore);
	readonly #userStore = inject(UserStore);

	public survey = signal(this.#surveyStore.survey());
	public selectedOnlyApv = signal(this.#surveyStore.selectedOnlyApv());

	protected isHealthChecker = this.#surveyStore.isHealthChecker;

	readonly hasCompletedFaceScan = computed(() => {
		// Check if survey includes face scan and we're on the last page
		// This means face scan was completed
		return (
			this.survey()?.IncludesFaceScan &&
			!this.#surveyStore.withoutFaceScan() &&
			!this.#surveyStore.selectedOnlyApv()
		);
	});

	readonly showPreviousButton = computed(() => {
		// Hide previous button if face scan was completed
		return !this.hasCompletedFaceScan();
	});

	constructor() {
		const apiService = inject(ApiService);
		const userService = inject(UserStorageService);
		const userDataService = inject(UserService);
		const companyService = inject(CompanyService);
		const healthPartnerService = inject(HealthpartnerService);
		const translate = inject(TranslateService);
		const location = inject(Location);
		const healthProfilService = inject(HealthprofilService);

		this.apiservice = apiService;
		this.userdataService = userDataService;
		this.companyservice = companyService;
		this.healthpartnerService = healthPartnerService;
		this.userservice = userService;
		this.translate = translate;
		this.location = location;
		this.healthprofilService = healthProfilService;
	}

	ngOnInit(): void {
		const lang = this.userservice.getLanguage();
		this.translate.use(lang.shortcode);
	}

	back(): void {
		const survey = this.#surveyStore.survey();
		const surveyId = survey?.Id;

		if (!surveyId || !survey) {
			// Fallback to browser history if no survey available
			window.history.back();
			return;
		}

		// Find the last question of the screening group (GroupType 0)
		const questions = this.#surveyStore.questions();
		const allQuestionGroups = this.#surveyStore.questionGroups();

		// Find ALL groups with GroupType 0 (Normal/Screening)
		const screeningGroups =
			allQuestionGroups?.filter((qg) => qg.GroupType === GroupType.Normal) ??
			[];

		if (screeningGroups.length > 0 && questions && questions.length > 0) {
			// Find all questions in ALL screening groups
			const questionsInScreeningGroups = questions.filter((q: Question) =>
				screeningGroups.some((sg) =>
					sg.QuestionItems.find((qi: any) => qi.Id === q.Id),
				),
			);

			if (questionsInScreeningGroups.length > 0) {
				// Get the last answered question (highest index) in screening groups
				const answeredQuestionsInScreening = questionsInScreeningGroups.filter(
					(q: Question) =>
						q.answer !== null &&
						q.answer !== undefined &&
						q.answer !== "" &&
						(!Array.isArray(q.answer) || q.answer.length > 0),
				);

				const lastScreeningQuestion =
					answeredQuestionsInScreening.length > 0
						? answeredQuestionsInScreening.reduce(
								(last: Question | null, current: Question) => {
									if (!last) return current;
									return current.index > last.index ? current : last;
								},
								null as Question | null,
							)
						: questionsInScreeningGroups.reduce(
								(last: Question | null, current: Question) => {
									if (!last) return current;
									return current.index > last.index ? current : last;
								},
								null as Question | null,
							);

				if (lastScreeningQuestion) {
					// Set the current question first, before navigation
					this.#surveyStore.setCurrentQuestion(lastScreeningQuestion);
					// Navigate to survey page with fromTransition flag to preserve the question
					this.router.navigate(["/survey", surveyId], {
						queryParams: { fromTransition: "true" },
					});
					return;
				}
			}
		}

		// Fallback: use browser history if we can't find the screening question
		window.history.back();
	}

	finish = async () => {
		console.log("survey", this.survey());
		console.log("questions", this.#surveyStore.questions());
		if (
			!this.#surveyStore.isHealthChecker() &&
			this.survey()?.IncludesHealthCheck
		) {
			this.Preparing = true;
			this.pleaseWait = true;
			await this.apiservice.LockUserResponse(this.survey()!.Id);
			this.router.navigate(["userinfo"]);
			return;
		}
		this.saveAllAnswers(true);
	};

	redirectToPersonalizedTraining = () => {
		this.healthprofilService.setHasLoadedSurveyReport(false);
		this.healthprofilService.fetchHealthProfileData().then(() => {
			this.router.navigate(["training"]);
		});
	};

	saveAllAnswers(finish: any) {
		this.Preparing = true;
		this.pleaseWait = true;

		const surveyResponseDto = {
			Answers: this.#surveyStore
				.questions()
				?.map((q: Question) => ({
					QuestionId: q.Id,
					Value: q.answer,
				}))
				.filter(
					(a) =>
						a.Value &&
						![null, undefined, ""].includes(a.Value.toString()) &&
						(!Array.isArray(a.Value) || a.Value.length > 0),
				),
			Survey: {
				Id: this.survey()?.Id,
			},
			QuestionGroup: this.survey()?.IncludesHealthCheck
				? GroupType.HealthCheck
				: this.#surveyStore.currentGroup()?.GroupType,
			SelectedAPVOnly: this.#surveyStore.selectedOnlyApv(),
		};

		this.apiservice
			.SendResponse(surveyResponseDto)
			.then((dataSurveyResponse: any) => {
				if (
					(this.survey()?.IncludesAPV && this.#surveyStore.selectedOnlyApv()) ||
					(this.survey()?.IncludesENPS && finish == true)
				) {
					this.apiservice.SendUserMail(dataSurveyResponse.Survey.Id);
					if (this.survey()?.IncludesScreening == true) {
						if (this.survey()?.IncludesAPV) {
							if (this.userdataService.user) {
								this.userdataService.user.HasCompletedWpa = true;
							}
							this.#userStore.setHasCompletedWpa(true);
						}
						this.healthprofilService.setHasLoadedSurveyReport(true);

						this.apiservice
							.GetCustomer(this.userdataService.getUser().Id)
							.then((customer: any) => {
								this.companyservice.setHasExtraBooking(
									customer.HasExtraBookingHours,
								);
								this.companyservice.setHasIntegrations(
									customer.Integrations > 0,
								);
								this.companyservice.setHasFollowUpFeature(
									customer.HasFollowUpFeature,
								);
								this.companyservice.setHasOffendingActFeature(
									customer.HasOffendingActFeature,
								);
								this.companyservice.setHasWellbeingFeature(
									customer.HasWellbeingFeature,
								);
								this.userdataService.setHasActiveSurvey(false);
								if (this.userdataService.user) {
									this.userdataService.user.HasCompletedSurvey = true;
								}

								if (this.trainingService.getHasTakePersonalTrainingSurvey()) {
									this.redirectToPersonalizedTraining();
								} else {
									this.router.navigate(["home"]);
								}
							});
					}

					this.finishedAPV = true;
					this.Preparing = false;
					this.pleaseWait = true;
					if (this.survey()?.IncludesAPV) {
						this.#userStore.setHasCompletedWpa(true);
						if (this.userdataService.user) {
							this.userdataService.user.HasCompletedWpa = true;
						}
						if (this.userdataService.user) {
							this.userdataService.user.HasCompletedSurvey = true;
						}
						this.router.navigate(["home"]);
					}
				} else if (finish == true) {
					this.apiservice.SendUserMail(dataSurveyResponse.Survey.Id);
					if (this.survey()?.IncludesAPV) {
						this.#userStore.setHasCompletedWpa(true);
						if (this.userdataService.user) {
							this.userdataService.user.HasCompletedWpa = true;
						}
					}
					this.Preparing = false;
					this.userdataService.setShowIntro(true);
					this.userdataService.setHighlightFocusArea(true);
					if (this.userdataService.user) {
						this.userdataService.user.HasCompletedSurvey = true;
					}

					if (this.trainingService.getHasTakePersonalTrainingSurvey()) {
						this.redirectToPersonalizedTraining();
					} else {
						this.router.navigate(["home"]);
					}
				}
			});
	}
}
